import request from "graphql-request";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useGraphQLContext } from "../../context/graphQLContext";
import { getCurrentUserId, getUserAddresses, getUserSettings } from "../../queries/users";
import { useQueryContext } from "../../context/queryContext";

export type Address = {
    addressCity: string;
    addressName: string;
    country: string | null;
    id: string;
    postcode: string;
    streetAndNumber: string;
    supplement: string | null;
    title: string;
    slug: string;
};

export type UserInfo = {
    id: string;
    billingAddress: Array<{ id: string }>;
    shippingAddress: Array<{ id: string }>;
    userAddresses: Array<Address>;
};

export type DefaultAddressIn = {
    userId: string;
    addressId: string;
};

export type DefaultAddressOut = {
    id: string;
};

export type UserSettings = {
    users: Array<{
        shippingCost: number | null;
        tax: number | null;
    }>;
};

export type UserId = {
    users: Array<{
        id: string;
    }>;
};

export const useGetUserInfo = () => {
    const graphqlOptions = useGraphQLContext();
    return useQuery<{ users: Array<UserInfo> }>({
        queryKey: ["userInfo"],
        queryFn: async () => {
            return request({
                ...graphqlOptions,
                document: getUserAddresses,
            });
        },
        keepPreviousData: true,
    });
};

const saveNewDefaultAddress = async (data, field, csrf) => {
    const url = "/";
    const formData = new FormData();

    formData.append("action", "users/save-user");
    formData.append("CRAFT_CSRF_TOKEN", csrf);
    formData.append("userId", data.userId);
    formData.append(`fields[${field}][]`, data.addressId);

    return fetch(url, {
        method: "POST",
        headers: {
            Accept: "application/json",
        },
        body: formData,
    });
};

export const useSaveUserBillingAddress = () => {
    const queryClient = useQueryClient();
    const queryOptions = useQueryContext();

    return useMutation({
        mutationKey: [],
        mutationFn: async (data: DefaultAddressIn) => {
            return saveNewDefaultAddress(data, "billingAddress", queryOptions.csrf);
        },
        onSuccess: () => {
            return queryClient.invalidateQueries({ queryKey: ["userInfo"] });
        },
    });
};

export const useSaveUserShippingAddress = () => {
    const queryClient = useQueryClient();
    const queryOptions = useQueryContext();

    return useMutation({
        mutationKey: [],
        mutationFn: async (data: DefaultAddressIn) => {
            return saveNewDefaultAddress(data, "shippingAddress", queryOptions.csrf);
        },
        onSuccess: () => {
            return queryClient.invalidateQueries({ queryKey: ["userInfo"] });
        },
    });
};

export const useGetUserSettings = (id: string) => {
    const graphqlOptions = useGraphQLContext();
    return useQuery<UserSettings>({
        queryKey: ["userSettings"],
        queryFn: async () => {
            return request({
                ...graphqlOptions,
                document: getUserSettings,
            });
        },
        enabled: !!id && id !== "",
        keepPreviousData: true,
        refetchOnWindowFocus: false,
    });
};

export const useGetCurrentUserId = () => {
    const graphqlOptions = useGraphQLContext();
    return useQuery<UserId>({
        queryKey: ["currentUser"],
        queryFn: async () => {
            return request({
                ...graphqlOptions,
                document: getCurrentUserId,
            });
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        retry: false,
    });
};
